import _ from "lodash";
import {
  setChildId,
  setParentNodeDetails,
  setPreviouslyVisitedNodes,
  setAdminNavigationNodes,
} from "./AdminNavigationActions";
import { goToRelativeRoute, openLinkInNewTab } from "modules/Services";
import {
  getInitialNode,
  modulesWithFullScreenModals,
} from "../AdminNavigationSidebarUtils";
import { setCurrentPageTitle } from "Login/modules/LoginModule";

const getRoute = ({ nodes, key }) => {
  const nodesByKey = _.keyBy(nodes, "id");

  let currentKey = key;
  const routeParts = [];

  while (currentKey) {
    const item = nodesByKey[currentKey];
    if (!item) break;

    if (item.route && !item.excludeRouteFromPath) {
      routeParts.unshift(item.route);
    }

    const parent = _.find(nodes, eachNode =>
      _.includes(eachNode.children, currentKey)
    );
    currentKey = parent ? parent.id : null;
  }

  return routeParts.join("/");
};

export const handleAdminSidebarMenuItemClickV2 = ({ key, searchText }) => {
  return (dispatch, getState) => {
    const state = getState();

    const nodes = _.get(state, "adminNavigation.nodes", []);

    const { childId, previouslyVisitedNodes } = _.get(
      state,
      "adminNavigation",
      {}
    );

    const menuItemsByKey = _.keyBy(nodes, "id");

    const currentMenu = _.find(nodes, { id: key });

    const currentMenuLabel = _.get(currentMenu, "label", "");

    const isSliderMenu = _.get(menuItemsByKey, [key, "isSliderSubmenu"], false);

    const openPageInFullScreenModal = !!_.find(
      modulesWithFullScreenModals,
      id => _.includes(key, id) || key === id
    );

    const levelId = _.get(menuItemsByKey, [key, "levelId"], false);

    let newPath = !_.isEmpty(searchText)
      ? getRoute({ nodes, key })
      : _.get(menuItemsByKey, [key, "route"], "");

    const oldPath = _.get(menuItemsByKey, [childId, "route"], "");

    // For nodes with excludeRouteFromPath, the parent's route is kept for search
    // discoverability but should not be part of the URL path construction.
    const excludeRouteFromPath = _.get(
      menuItemsByKey,
      [key, "excludeRouteFromPath"],
      false
    );
    if (excludeRouteFromPath) {
      newPath = "";
    }

    if (isSliderMenu) {
      let selectedNode = _.get(menuItemsByKey, [key], {});
      const sliderMenuNode = selectedNode;
      /**
       * While selected node is not a leaf node, keep traversing down the tree
       * to find the first leaf node.
       * Check if node has a defaultChildId to navigate to, otherwise use first child.
       */
      while (!_.isEmpty(_.get(selectedNode, "children", []))) {
        const childrenIds = _.get(selectedNode, "children", []);
        // Use defaultChildId if specified on the node, otherwise default to first child
        const nextChildId =
          _.get(selectedNode, "defaultChildId") || _.get(childrenIds, [0], "");

        selectedNode = _.get(menuItemsByKey, [nextChildId], {});
      }

      const newChildId = _.get(selectedNode, "id", "");
      const newChildNodeRoute = _.get(selectedNode, "route", "");

      if (newChildNodeRoute) {
        newPath = newPath
          ? `${newPath}/${newChildNodeRoute}`
          : newChildNodeRoute;
      }

      let parentNode = currentMenu;

      if (searchText && sliderMenuNode.levelId === "L2") {
        // If search text is present, we can directly jump into multiple levels of navigation.
        // therefore if we go to a L3 node. In that case, the parent won't be current menu
        parentNode = _.find(nodes, eachNode =>
          _.includes(eachNode.children, sliderMenuNode.id)
        );
      }

      dispatch(setChildId(newChildId));
      dispatch(
        setCurrentPageTitle({ label: _.get(selectedNode, "label", "") })
      );
      dispatch(setParentNodeDetails(parentNode));
      dispatch(
        setPreviouslyVisitedNodes(previouslyVisitedNodes.concat(childId))
      );
    } else if (openPageInFullScreenModal) {
      // For full screen modals, we need to store old childId as previously visited node. This is required because
      // there is no way to click on sidebar menu as it will be covered by the modal. Therefore, we need to provide
      // support for back navigation which requires to track previously visited nodes.
      dispatch(
        setPreviouslyVisitedNodes(previouslyVisitedNodes.concat(childId))
      );
      dispatch(setCurrentPageTitle({ label: currentMenuLabel }));
      dispatch(setChildId(key));
    } else {
      if (!_.isEmpty(searchText)) {
        if (levelId === "L2") {
          const parentNode = _.find(nodes, eachNode =>
            _.includes(eachNode.children, key)
          );
          dispatch(setParentNodeDetails(parentNode));
        }
        if (levelId === "L3") {
          const parentNode = _.find(nodes, eachNode =>
            _.includes(
              eachNode.children,
              _.find(nodes, n => _.includes(n.children, key))?.id
            )
          );
          dispatch(setParentNodeDetails(parentNode));
        }
        dispatch(
          setPreviouslyVisitedNodes(previouslyVisitedNodes.concat(childId))
        );
      }
      dispatch(setCurrentPageTitle({ label: currentMenuLabel }));
      dispatch(setChildId(key));
    }

    /**
     * Why we are doing this?
     * replace paramater in goToRelativeRoute can't accept a path like `abc/def`. Therefore, we are passing the first segment of the path
     * to correctly replace the entire old path with the new path.
     */
    const firstSegmentOfOldPath = _.first(_.split(oldPath, "/"));

    dispatch(
      goToRelativeRoute({
        type: "replace",
        route: newPath,
        replacePath: firstSegmentOfOldPath,
      })
    );
  };
};

export const updateChildId = childId => {
  return (dispatch, getState) => {
    const state = getState();
    const { nodes, childId: oldChildId } = _.get(state, "adminNavigation", {});

    const newChildNode = _.find(nodes, { id: childId });

    const oldChildNode = _.find(nodes, { id: oldChildId });

    const doesNewNodeHasLevelL2OrL3 =
      newChildNode?.levelId === "L2" || newChildNode?.levelId === "L3";

    const doesOldNodeHasLevelL2OrL3 =
      oldChildNode?.levelId === "L2" || oldChildNode?.levelId === "L3";

    if (doesNewNodeHasLevelL2OrL3 && doesOldNodeHasLevelL2OrL3) {
      /**
       * Why this step is needed?
       * In adminNavigation there are instances where we navigate between sidebar options without clicking on them.
       * Here, we need to maintain the previously visited nodes correctly and parentNodeDetails accordingly so that
       * state is consistent and back button works as expected.
       */
      dispatch(setPreviouslyVisitedNodes([]));

      dispatch(setParentNodeDetails(null));
    }

    dispatch(setChildId(childId));
  };
};

export const handleAdminSidebarBackButtonClickV2 = () => {
  return (dispatch, getState) => {
    const state = getState();

    const { childId, previouslyVisitedNodes, nodes, parentNodeDetails } = _.get(
      state,
      "adminNavigation",
      {}
    );

    /**
     * This case is applicable when user is navigating via sidebar menu
     */
    if (!_.isEmpty(previouslyVisitedNodes)) {
      const newChildId = _.last(previouslyVisitedNodes);

      dispatch(setChildId(newChildId));

      const newChildNode = _.find(nodes, { id: newChildId });

      dispatch(
        setCurrentPageTitle({ label: _.get(newChildNode, "label", "") })
      );

      let oldNode;

      const isCurrentNodeOpenInFullScreenModal = !!_.find(
        modulesWithFullScreenModals,
        id => _.includes(childId, id) || childId === id
      );
      if (isCurrentNodeOpenInFullScreenModal) {
        /**
         * This case is applicable when user is navigating via sidebar menu
         * and the current node is open in full screen modal.
         * In this case, we need to consider the childId as the old node
         * because we need to replace its path with the new path.
         */
        oldNode = _.find(nodes, { id: childId });
      } else {
        /**
         * This case is applicable when user has navigated a node via slider submenu.
         * In this case, we consider the parentNodeDetails as the old node as we
         * need to replace path from the parent node's route, not from child's route.
         */
        oldNode = parentNodeDetails;
      }

      // For excludeRouteFromPath parents or route-less parents (e.g., accordion
      // headers), the parent's route doesn't reflect the actual URL structure.
      // Fall back to the current child's route, which carries the full path
      // (e.g., "schoolId/educatorcenter_v2").
      let oldPath = _.get(oldNode, "route", "");
      if (_.isEmpty(oldPath) || _.get(oldNode, "excludeRouteFromPath")) {
        const currentChild = _.find(nodes, { id: childId });
        oldPath = _.get(currentChild, "route", oldPath);
      }
      const newPath = _.get(newChildNode, "route", "");

      dispatch(setPreviouslyVisitedNodes(previouslyVisitedNodes.slice(0, -1)));
      const parentNode =
        _.find(nodes, eachNode =>
          _.includes(eachNode.children, newChildNode.id)
        ) || null;

      dispatch(setParentNodeDetails(parentNode));

      /**
       * Why we are doing this?
       * replace paramater in goToRelativeRoute can't accept a path like `abc/def`. Therefore, we are passing the first segment of the path
       * to correctly replace the entire old path with the new path.
       */
      const firstSegmentOfOldPath = _.first(_.split(oldPath, "/"));

      dispatch(
        goToRelativeRoute({
          type: "replace",
          route: newPath,
          replacePath: firstSegmentOfOldPath,
        })
      );
    } else {
      /**
       * This case is applicable when user directly lands on a screen via URL
       * and then clicks on back button.
       */

      /**
       * Find node having levelId as L1. We will consider its route as replacePath parameter.
       */

      let possibleL1Node = _.find(nodes, { id: childId });

      while (_.get(possibleL1Node, "levelId") !== "L1") {
        possibleL1Node = _.find(nodes, ({ children }) =>
          _.includes(children, possibleL1Node.id)
        );
      }

      // For excludeRouteFromPath L1 nodes, the route doesn't reflect
      // the actual URL structure. Use the current child's route instead.
      let routeToReplace = _.get(possibleL1Node, "route", "");
      if (_.get(possibleL1Node, "excludeRouteFromPath")) {
        const currentChild = _.find(nodes, { id: childId });
        routeToReplace = _.get(currentChild, "route", routeToReplace);
      }

      /**
       * Why we are doing this?
       * replace paramater in goToRelativeRoute can't accept a path like `abc/def`. Therefore, we are passing the first segment of the path
       * to correctly replace the entire old path with the new path.
       */
      const firstSegmentOfRouteToReplace = _.first(
        _.split(routeToReplace, "/")
      );

      const initialNode = getInitialNode(nodes);

      dispatch(setChildId(initialNode.id));
      dispatch(setCurrentPageTitle({ label: _.get(initialNode, "label", "") }));

      dispatch(
        goToRelativeRoute({
          type: "replace",
          route: initialNode.route,
          replacePath: firstSegmentOfRouteToReplace,
        })
      );
    }
  };
};

export const clearAdminNavigationState = () => {
  return dispatch => {
    dispatch(setChildId(null));
    dispatch(setParentNodeDetails(null));
    dispatch(setPreviouslyVisitedNodes([]));
    dispatch(setAdminNavigationNodes([]));
  };
};

export const goToAdminNavigationChildRoute = ({
  route,
  isOpenInNewTab = false,
  navigateType = "push",
  adminNavigationTab = null,
  addProgramPrefixForAccountSettingsTab = false,
  curriculumProgramId: curriculumProgramIdProp = null,
}) => {
  return (dispatch, getState) => {
    const activeTab =
      adminNavigationTab || _.get(getState(), "login.activeTab", "");

    const curriculumProgramId =
      curriculumProgramIdProp ||
      _.get(getState(), "platform.currentCurriculumProgram.id", "");

    if (activeTab === "accountSettingsTab") {
      const routePath = addProgramPrefixForAccountSettingsTab
        ? `/platform/accountSettings/${curriculumProgramId}/${route}`
        : `/platform/accountSettings/${route}`;
      if (isOpenInNewTab) {
        dispatch(openLinkInNewTab({ url: routePath }));
      } else {
        dispatch(
          goToRelativeRoute({
            route: routePath,
            type: navigateType,
          })
        );
      }
    } else {
      const routePath = `/platform/school/${curriculumProgramId}/${route}`;
      if (isOpenInNewTab) {
        dispatch(openLinkInNewTab({ url: routePath }));
      } else {
        dispatch(
          goToRelativeRoute({
            route: routePath,
            type: navigateType,
          })
        );
      }
    }
  };
};
